import React, { useState, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Col, Row, Button, Card, Form, InputGroup, ButtonGroup } from '@themesberg/react-bootstrap';

import moment from 'moment-timezone';
import Datetime from 'react-datetime';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { convertDate, simpleDecrypt } from 'utils/helpers';
import { useDispatch, useSelector } from 'react-redux';
import { eventAddSaga, eventSingleShowSaga } from 'reduxToolkit/actions/event/eventAction';
import { validationMsg } from '../utils/constants';
import { storeTypes } from '../types';
import { eventAddSlice } from '../reduxToolkit/reducers';
import { useHistory, useParams } from 'react-router-dom';
import { config } from 'config';

const EventForm = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { eventId } = useParams<{ eventId: string }>();

    const { eventAddState, eventSingleShowState } = useSelector((state: storeTypes.IStore) => state);
    const { isEventAddSuccess } = eventAddState;
    const { data, isEventSingleShowSuccess } = eventSingleShowState;

    const { eventAddReset } = eventAddSlice.actions;

    //Values
    const [title, setTitle] = useState('');
    const [shortDescription, setShortDescription] = useState('');
    const [description, setDescription] = useState('');
    const [startDate, setStartDate] = useState<any>('');
    const [endDate, setEndDate] = useState<any>('');
    const [duration, setDuration] = useState('');
    const [fees, setFees] = useState('');
    const [offer, setOffer] = useState('');
    const [whoCanAttend, setWhoCanAttend] = useState('');
    const [type, setType] = useState('');
    const [uploadFile, setUploadFile] = useState<any>(null);
    const [fileURL, setFileURL] = useState<any>('');

    const [validation, setValidation] = useState(false);

    const dispatchEvent = useCallback(() => {
        if (
            !title ||
            !shortDescription ||
            !description ||
            !startDate ||
            !endDate ||
            !duration ||
            !fees ||
            !offer ||
            !whoCanAttend ||
            !type
        ) {
            setValidation(true);
            return;
        }
        setValidation(false);
        const payload = {
            id: data?.id || null,
            title: title,
            short_description: shortDescription,
            description: description,
            start_date: convertDate(startDate, 'YYYY-MM-DD'),
            end_date: convertDate(endDate, 'YYYY-MM-DD'),
            start_time: convertDate(startDate, 'HH:MM'),
            end_time: convertDate(endDate, 'HH:MM'),
            duration,
            fees,
            offer,
            who_can_attend: whoCanAttend,
            type,
            uploadFile: uploadFile,
        };
        dispatch(eventAddSaga(payload));
    }, [
        title,
        shortDescription,
        description,
        startDate,
        endDate,
        duration,
        fees,
        offer,
        whoCanAttend,
        type,
        uploadFile,
    ]);

    useEffect(() => {
        if (isEventAddSuccess === true) {
            setTimeout(() => {
                dispatch(eventAddReset());
                history.push('/events');
            }, 1000);
        }
    }, [isEventAddSuccess]);

    //Watch Params
    useEffect(() => {
        if (eventId) {
            const event_Id = simpleDecrypt(eventId);
            const dispatchPayload = {
                eventId: event_Id,
                type: 'view',
            };
            dispatch(eventSingleShowSaga(dispatchPayload));
        }
    }, [eventId]);

    useEffect(() => {
        if (isEventSingleShowSuccess === true) {
            setTitle(data?.title || '');
            setShortDescription(data?.short_description || '');
            setDescription(data?.description || '');
            setDuration(data?.duration || '');
            setFees(data?.fees || '');
            setOffer(data?.offer || '');
            setWhoCanAttend(data?.who_can_attend || '');
            setType(data?.type || '');
            setFileURL(`${config.apiBaseURL}/${data?.file_path}` || '');

            const customStartDate = `${data?.start_date}T${data?.start_time}:00.000Z`;
            const customEndDate = `${data?.end_date}T${data?.end_time}:00.000Z`;
            setStartDate(customStartDate || '');
            setEndDate(customEndDate || '');
        }
    }, [isEventSingleShowSuccess, data]);

    return (
        <>
            <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                <div className="d-block mb-4 mb-md-0">
                    <h5>Event {eventId ? 'Edit' : 'Add'} information</h5>
                </div>
                <div className="btn-toolbar mb-2 mb-md-0">
                    <ButtonGroup>
                        <a href="/events">
                            <Button variant="outline-primary" size="sm">
                                Back
                            </Button>
                        </a>
                    </ButtonGroup>
                </div>
            </div>

            <Row>
                <Col xs={12} xl={8}>
                    <Card border="light" className="bg-white shadow-sm mb-4">
                        <Card.Body>
                            {/* Title and description */}
                            <Row>
                                <Col md={12} className="mb-3">
                                    <Form.Group id="firstName">
                                        <Form.Label>Title</Form.Label>
                                        <Form.Control
                                            value={title}
                                            onChange={(e) => {
                                                setTitle(e.target.value);
                                            }}
                                            required
                                            as="textarea"
                                            placeholder="Type here"
                                        />
                                        {validation && !title && (
                                            <span className="error_text">{validationMsg.REQUIRED}</span>
                                        )}
                                    </Form.Group>
                                </Col>

                                <Col md={12} className="mb-3">
                                    <Form.Group id="firstName">
                                        <Form.Label>Short Description</Form.Label>
                                        <Form.Control
                                            value={shortDescription}
                                            onChange={(e) => setShortDescription(e.target.value)}
                                            required
                                            as="textarea"
                                            placeholder="Type here"
                                        />
                                        {validation && !shortDescription && (
                                            <span className="error_text">{validationMsg.REQUIRED}</span>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={12} className="mb-3">
                                    <Form.Group id="firstName">
                                        <Form.Label>Long Description</Form.Label>

                                        <ReactQuill
                                            theme="snow"
                                            value={description}
                                            onChange={setDescription}
                                            style={{ height: '200px' }}
                                        />
                                    </Form.Group>
                                    {validation && !description && (
                                        <span className="error_text">{validationMsg.REQUIRED}</span>
                                    )}
                                    <br />
                                    <br />
                                </Col>
                            </Row>

                            {/* Start and end date */}
                            <Row className="align-items-center">
                                <Col md={6} className="mb-3">
                                    <Form.Group id="birthday">
                                        <Form.Label>Start Date and Time</Form.Label>

                                        <Datetime
                                            onChange={(value: any) => setStartDate(value)}
                                            closeOnSelect={true}
                                            renderInput={(props, openCalendar) => (
                                                <InputGroup>
                                                    <InputGroup.Text>
                                                        <FontAwesomeIcon icon={faCalendarAlt} />
                                                    </InputGroup.Text>
                                                    <Form.Control
                                                        required
                                                        type="text"
                                                        value={
                                                            startDate
                                                                ? moment(startDate).format('MM/DD/YYYY hh:mm A')
                                                                : ''
                                                        }
                                                        placeholder="Start Date and Time"
                                                        onFocus={() => openCalendar}
                                                    />
                                                </InputGroup>
                                            )}
                                        />
                                        {validation && !startDate && (
                                            <span className="error_text">{validationMsg.REQUIRED}</span>
                                        )}
                                    </Form.Group>
                                </Col>

                                <Col md={6} className="mb-3">
                                    <Form.Group id="birthday">
                                        <Form.Label>End Date and Time</Form.Label>
                                        <Datetime
                                            onChange={(value: any) => setEndDate(value)}
                                            closeOnSelect={true}
                                            renderInput={(props, openCalendar) => (
                                                <InputGroup>
                                                    <InputGroup.Text>
                                                        <FontAwesomeIcon icon={faCalendarAlt} />
                                                    </InputGroup.Text>
                                                    <Form.Control
                                                        required
                                                        type="text"
                                                        value={
                                                            endDate ? moment(endDate).format('MM/DD/YYYY hh:mm A') : ''
                                                        }
                                                        placeholder="End Date and Time"
                                                        onFocus={() => openCalendar}
                                                    />
                                                </InputGroup>
                                            )}
                                        />
                                        {validation && !endDate && (
                                            <span className="error_text">{validationMsg.REQUIRED}</span>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* Fees and Offer */}
                            <Row>
                                <Col md={6} className="mb-3">
                                    <Form.Group id="firstName">
                                        <Form.Label>Fees</Form.Label>
                                        <Form.Control
                                            value={fees}
                                            onChange={(e) => setFees(e.target.value)}
                                            required
                                            type="text"
                                            placeholder="Type here"
                                        />
                                        {validation && !fees && (
                                            <span className="error_text">{validationMsg.REQUIRED}</span>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6} className="mb-3">
                                    <Form.Group id="lastName">
                                        <Form.Label>Offer Price</Form.Label>
                                        <Form.Control
                                            value={offer}
                                            onChange={(e) => setOffer(e.target.value)}
                                            required
                                            type="text"
                                            placeholder="Type here"
                                        />
                                        {validation && !offer && (
                                            <span className="error_text">{validationMsg.REQUIRED}</span>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6} className="mb-3">
                                    <Form.Group id="gender">
                                        <Form.Label>Type</Form.Label>
                                        <Form.Select
                                            onChange={(e: any) => setType(e.target.value)}
                                            defaultValue="online"
                                        >
                                            <option value="online">Online</option>
                                            <option value="offline">Offline</option>
                                        </Form.Select>
                                        {validation && !type && (
                                            <span className="error_text">{validationMsg.REQUIRED}</span>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6} className="mb-3">
                                    <Form.Group id="firstName">
                                        <Form.Label>Who can attend</Form.Label>
                                        <Form.Control
                                            value={whoCanAttend}
                                            onChange={(e) => setWhoCanAttend(e.target.value)}
                                            required
                                            type="text"
                                            placeholder="Type here"
                                        />
                                        {validation && !whoCanAttend && (
                                            <span className="error_text">{validationMsg.REQUIRED}</span>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6} className="mb-3">
                                    <Form.Group id="firstName">
                                        <Form.Label>Duration (In Days)</Form.Label>
                                        <Form.Control
                                            value={duration}
                                            onChange={(e) => setDuration(e.target.value)}
                                            required
                                            type="text"
                                            placeholder="Type here"
                                        />
                                        {validation && !duration && (
                                            <span className="error_text">{validationMsg.REQUIRED}</span>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                            <div className="mt-3">
                                <Button onClick={() => dispatchEvent()} variant="secondary" type="button">
                                    Submit
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12} xl={4}>
                    <Row>
                        <Col xs={12}>
                            <Form.Group controlId="formFile" className="mb-3">
                                <Form.Label>Image</Form.Label>
                                <Form.Control
                                    onChange={(e: any) => {
                                        const files = e.target.files;
                                        const file = files.length ? files[0] : null;
                                        const file_URL = URL.createObjectURL(file);
                                        setUploadFile(file);
                                        setFileURL(file_URL);
                                    }}
                                    type="file"
                                />
                            </Form.Group>

                            {fileURL ? (
                                <Card border="light" className="text-center p-0 mb-4">
                                    <div
                                        style={{ backgroundImage: `url(${fileURL})` }}
                                        className="profile-cover rounded-top"
                                    />
                                </Card>
                            ) : null}
                        </Col>
                    </Row>
                </Col>
            </Row>
        </>
    );
};

export default EventForm;
